import React from "react";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { STEP, branchSteps, countTasks, hasWalletError } from "./branchState";
import "./BranchProgress.css";

const STEP_ICON = {
  [STEP.WAIT]: <ClockCircleOutlined />,
  [STEP.SKIP]: <ClockCircleOutlined />,
  [STEP.ACTIVE]: <LoadingOutlined spin />,
  [STEP.DONE]: <CheckCircleOutlined />,
  [STEP.ERROR]: <CloseCircleOutlined />,
};

function Step({ state, title, subtitle }) {
  return (
    <div className="bp-step" data-state={state}>
      <span className="bp-dot">{STEP_ICON[state]}</span>
      <div className="bp-text">
        <b>{title}</b>
        <span>{subtitle}</span>
      </div>
    </div>
  );
}

function Connector({ filled }) {
  return (
    <div className={`bp-conn${filled ? " is-on" : ""}`}>
      <i />
    </div>
  );
}

function progressLabel({ done, total }) {
  return total ? `${done} of ${total} tasks` : "Starting...";
}

function failureMessages(main, plan) {
  const messages = [];
  if (hasWalletError(plan)) {
    messages.push(
      `The ${plan.currency_wallet.currency} wallet could not be created (${plan.currency_wallet.error}). The rest of the setup continues without it.`
    );
  }
  if (plan.state === "skipped") {
    messages.push(plan.error);
  } else if (plan.state === "error") {
    if (plan.failed_step === "link") {
      messages.push(
        `${plan.name} is ready but could not join the organisation (${plan.error}). Retry from Settings, Add child entity.`
      );
    } else if (plan.mode === "attach") {
      messages.push(
        `${plan.name} could not be attached (${plan.error}). Retry from Settings, Add child entity.`
      );
    } else {
      messages.push(
        `${main.company_name} is ready and works on its own. ${plan.error}. Attach a company later from Settings, Add child entity.`
      );
    }
  }
  return messages;
}

function formatAmount(wallet) {
  return `${wallet.amount.toLocaleString("en-US")} ${wallet.currency}`;
}

function buildSteps(main, plan, branch, states) {
  const steps = [];

  if (plan.mode === "create") {
    steps.push({
      key: "main",
      state: states.main,
      title: main.company_name,
      subtitle:
        states.main === STEP.ACTIVE
          ? progressLabel(countTasks(main))
          : states.main === STEP.ERROR
          ? "Failed"
          : "Ready",
    });
  }

  if (plan.currency_wallet) {
    const wallet = plan.currency_wallet;
    steps.push({
      key: "wallet",
      state: states.wallet,
      title: `${wallet.currency} wallet`,
      subtitle: {
        [STEP.WAIT]: `Created once ${main.company_name} is ready`,
        [STEP.ACTIVE]: `Creating and loading ${formatAmount(wallet)}...`,
        [STEP.DONE]: `${formatAmount(wallet)} loaded`,
        [STEP.ERROR]: "Creation failed",
        [STEP.SKIP]: "Not created",
      }[states.wallet],
    });
  }

  if (plan.mode === "create") {
    steps.push(
      {
        key: "branch",
        state: states.branch,
        title: plan.name,
        subtitle: {
          [STEP.WAIT]: `Created once ${main.company_name} is ready`,
          [STEP.SKIP]: "Not created",
          [STEP.ACTIVE]: progressLabel(countTasks(branch)),
          [STEP.ERROR]: "Creation failed",
          [STEP.DONE]: "Ready",
        }[states.branch],
      },
      {
        key: "link",
        state: states.link,
        title: "One organisation",
        subtitle: {
          [STEP.WAIT]: "Linked at the end",
          [STEP.SKIP]: "Not linked",
          [STEP.ACTIVE]: "Linking...",
          [STEP.DONE]: "Linked",
          [STEP.ERROR]: "Link failed",
        }[states.link],
      }
    );
  } else {
    steps.push({
      key: "link",
      state: states.link,
      title: `Attach ${plan.name}`,
      subtitle: {
        [STEP.WAIT]: `Starts once ${main.company_name} is ready`,
        [STEP.ACTIVE]: "Attaching...",
        [STEP.DONE]: "Attached",
        [STEP.ERROR]: "Attach failed",
        [STEP.SKIP]: "Not attached",
      }[states.link],
    });
  }

  return steps;
}

export function BranchProgress({ main, plan, branch }) {
  const states = branchSteps(main, plan);
  const steps = buildSteps(main, plan, branch, states);
  const messages = failureMessages(main, plan);

  return (
    <div className="bp-card" aria-label="Organisation progress">
      <div className="bp-top">
        <b>Organisation</b>
        <span>
          {plan.mode === "attach"
            ? `Runs after ${main.company_name}`
            : "Steps run one after the other"}
        </span>
      </div>
      <div className="bp-steps">
        {steps.map((step, index) => (
          <React.Fragment key={step.key}>
            {index > 0 && (
              <Connector filled={steps[index - 1].state === STEP.DONE} />
            )}
            <Step
              state={step.state}
              title={step.title}
              subtitle={step.subtitle}
            />
          </React.Fragment>
        ))}
      </div>
      {messages.map((message) => (
        <p className="bp-hint" key={message}>
          {message}
        </p>
      ))}
    </div>
  );
}

const ENTITY_DOT = {
  wait: "#d1d5db",
  active: "#f59e0b",
  done: "#10b981",
  error: "#ef4444",
  skip: "#d1d5db",
};

export function EntityTabs({ main, plan, selected, onSelect }) {
  const steps = branchSteps(main, plan);
  const items = [
    { key: "main", name: main.company_name, state: steps.main },
    { key: "branch", name: plan.name, state: steps.branch },
  ];
  return (
    <div className="bp-ents" role="tablist" aria-label="Companies">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={selected === item.key}
          className="bp-ent"
          onClick={() => onSelect(item.key)}
        >
          <span
            className="bp-ent-dot"
            style={{ backgroundColor: ENTITY_DOT[item.state] }}
          />
          <span>{item.name}</span>
          {item.state === STEP.ACTIVE && <span className="bp-now">Now</span>}
        </button>
      ))}
    </div>
  );
}
